// custom-input-select-searchable.js
import React, { useState, useRef, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Popover,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Box,
  ButtonBase,
  Typography,
} from "@material-ui/core"
import SearchIcon from "@material-ui/icons/Search"
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown"
import CheckIcon from "@material-ui/icons/Check"
import { makeStyles } from "@material-ui/core/styles"

const useStyles = makeStyles((theme) => ({
  trigger: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1, 0.5, 1.5),
    minWidth: 220,
    maxWidth: 420,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#fff",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    transition: "border-color 120ms ease, box-shadow 120ms ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    },
    "&$triggerOpen": {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}33`,
    },
    "&$triggerDisabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      cursor: "not-allowed",
      "&:hover": {
        borderColor: theme.palette.divider,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      },
    },
  },
  triggerOpen: {},
  triggerDisabled: {},
  triggerLabel: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontWeight: 500,
    color: theme.palette.text.primary,
    textAlign: "left",
  },
  triggerPlaceholder: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  caret: {
    color: theme.palette.text.secondary,
    transition: "transform 150ms ease",
  },
  caretOpen: {
    transform: "rotate(180deg)",
  },
  popover: {
    marginTop: theme.spacing(1),
  },
  popoverPaper: {
    width: 360,
    maxWidth: "90vw",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow:
      "0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08)",
  },
  searchWrap: {
    padding: theme.spacing(1.5, 1.5, 1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.palette.grey[50],
    },
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1, 1),
      fontSize: "0.875rem",
    },
  },
  list: {
    maxHeight: 280,
    overflowY: "auto",
    padding: theme.spacing(0.5, 0),
  },
  listItem: {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
  },
  listItemSelected: {
    backgroundColor: `${theme.palette.primary.light}22 !important`,
    "& .MuiListItemText-primary": {
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
  },
  checkIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.1rem",
    marginLeft: theme.spacing(1),
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}))

/**
 * CustomInputSelectSearchable — a compact, breadcrumb-friendly select.
 *
 * Renders a pill-shaped trigger that shows the current selection. Clicking
 * the trigger opens a popover containing a search field at the top and a
 * filterable list of options below.
 */
const CustomInputSelectSearchable = ({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  className = "",
  style = {},
}) => {
  const classes = useStyles()
  const triggerRef = useRef(null)
  const searchInputRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")

  const selected = options.find((opt) => opt.value === value) || null

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((opt) =>
      (opt.label || "").toLowerCase().includes(q),
    )
  }, [options, query])

  useEffect(() => {
    if (open && searchInputRef.current) {
      // Defer focus until popover has mounted
      const id = window.setTimeout(() => {
        searchInputRef.current && searchInputRef.current.focus()
      }, 0)
      return () => window.clearTimeout(id)
    }
    if (!open) setQuery("")
  }, [open])

  const handleOpen = () => {
    if (disabled) return
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleSelect = (optValue) => {
    onChange(optValue)
    setOpen(false)
  }

  return (
    <>
      <ButtonBase
        ref={triggerRef}
        focusRipple
        disabled={disabled}
        onClick={handleOpen}
        className={`${classes.trigger} ${open ? classes.triggerOpen : ""} ${
          disabled ? classes.triggerDisabled : ""
        } ${className}`}
        style={style}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Typography
          component="span"
          variant="body2"
          className={`${classes.triggerLabel} ${
            !selected ? classes.triggerPlaceholder : ""
          }`}
        >
          {selected ? selected.label : placeholder}
        </Typography>
        <ArrowDropDownIcon
          className={`${classes.caret} ${open ? classes.caretOpen : ""}`}
        />
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.popover}
        PaperProps={{ className: classes.popoverPaper }}
      >
        <Box className={classes.searchWrap}>
          <TextField
            inputRef={searchInputRef}
            variant="outlined"
            size="small"
            fullWidth
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {filteredOptions.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">No matches found</Typography>
          </Box>
        ) : (
          <List className={classes.list} dense>
            {filteredOptions.map((opt) => {
              const isSelected = selected && selected.value === opt.value
              return (
                <ListItem
                  key={opt.value}
                  button
                  selected={isSelected}
                  onClick={() => handleSelect(opt.value)}
                  className={classes.listItem}
                  classes={{ selected: classes.listItemSelected }}
                >
                  <ListItemText primary={opt.label} />
                  {isSelected && <CheckIcon className={classes.checkIcon} />}
                </ListItem>
              )
            })}
          </List>
        )}
      </Popover>
    </>
  )
}

CustomInputSelectSearchable.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
}

export default CustomInputSelectSearchable
